import { Link } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { build } from 'typage-url';
import { EnergyType } from '~/models/meter';
import { $ } from '~/routes/path';

export interface MeterLinkProps {
  meterId: string;
  meterNumber: string;
  energyType?: EnergyType;
  /** Additional text after meter number (e.g. alias) */
  suffix?: string;
  variant?: 'body1' | 'body2' | 'caption' | 'inherit';
  sx?: object;
}

/**
 * Renders a clickable link to the meter detail page (NMI or MIRN).
 * GAS meters → MIRN detail; others → NMI detail.
 */
const MeterLink: React.FC<MeterLinkProps> = ({
  meterId,
  meterNumber,
  energyType,
  suffix,
  variant = 'body2',
  sx = {}
}) => {
  const isGas = energyType === EnergyType.GAS;
  const to = build($.dashboard[isGas ? 'mirn' : 'nmi'].detail.id(meterId));

  return (
    <Link
      color="textPrimary"
      component={RouterLink}
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      sx={{ textDecoration: 'underline', ...sx }}
    >
      {meterNumber}
      {suffix}
    </Link>
  );
};

/**
 * Renders a list of meter numbers as links, joined by separator.
 */
export const MeterLinksList: React.FC<{
  meterNumbers: string[];
  meterSelections: Array<{ meterId: string; meterNumber: string }>;
  projectMeters: Array<{ id: string; meterNumber?: string; energyType?: string }>;
  separator?: string;
  variant?: 'body1' | 'body2' | 'caption' | 'inherit';
  sx?: object;
}> = ({ meterNumbers, meterSelections, projectMeters, separator = ', ', variant = 'body2', sx = {} }) => {
  return (
    <>
      {meterNumbers.map((mn, i) => {
        const sel = meterSelections.find((s) => s.meterNumber === mn);
        const meter = sel ? projectMeters.find((m) => m.id === sel.meterId) : undefined;
        return (
          <React.Fragment key={mn}>
            {i > 0 && separator}
            {sel ? (
              <MeterLink
                meterId={sel.meterId}
                meterNumber={mn}
                energyType={meter?.energyType as any}
                variant={variant}
                sx={sx}
              />
            ) : (
              mn
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default MeterLink;
